import { useCommunityChannel } from '../care-team/hooks/store/useChannelHooks';
import { ChannelItem } from '../care-team/channel/ChannelItem';
import { Box, Button, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Channel, PeerToPeerChannelAttribute } from '../care-team/type';
import { useNavigate } from 'react-router';
import { Routes } from '@xolv-mybrightlink/common';
import { useCommunityOptInStatus } from '../care-team/hooks/store/useCommunitySla';
import { CommunityOptInSlaStandalone } from './CommunityOptInSlaStandalone';
import { CommunityOptInSla } from './CommunityOptInSla';

type CommunityChannelListProps = {
  onSelect: (channelGuid: string) => void;
  standalone?: boolean;
};

export const CommunityChannelList = (props: CommunityChannelListProps) => {
  const optedInCommunity = useCommunityOptInStatus();
  const communityChannels = useCommunityChannel();
  const { standalone = false } = props;
  const onSelectChannel = (channelGuid: string) => () => {
    props.onSelect(channelGuid);
  };
  if (!optedInCommunity) {
    return standalone ? <CommunityOptInSlaStandalone /> : <CommunityOptInSla />;
  }
  const { active: activeChannels, disconnected: disconnectedChannels } =
    communityChannels;
  if (activeChannels.length === 0 && disconnectedChannels.length === 0) {
    return <EmptyCommunityChannel />;
  }
  return (
    <>
      {activeChannels.map((channel) => (
        <ChannelItem
          key={channel.guid}
          channelGuid={channel.guid}
          onSelect={onSelectChannel(channel.guid)}
        />
      ))}
      {disconnectedChannels.length > 0 && (
        <DisconnectedChannelSection
          channels={disconnectedChannels}
          onSelect={props.onSelect}
        />
      )}
    </>
  );
};

type DisconnectedChannelSectionProps = {
  channels: Channel<PeerToPeerChannelAttribute>[];
  onSelect: (channelGuid: string) => void;
};

const DisconnectedChannelSection = ({
  channels,
  onSelect,
}: DisconnectedChannelSectionProps) => {
  const onSelectChannel = (channelGuid: string) => () => {
    onSelect(channelGuid);
  };
  return (
    <>
      <Box sx={{ px: 5, pt: 8, pb: 5 }}>
        <Typography sx={{ fontWeight: 600 }}>
          <FormattedMessage id='community.messages.disconnected' />
        </Typography>
      </Box>
      {channels.map((channel) => (
        <ChannelItem
          key={channel.guid}
          channelGuid={channel.guid}
          onSelect={onSelectChannel(channel.guid)}
        />
      ))}
    </>
  );
};

const EmptyCommunityChannel = () => {
  const navigate = useNavigate();
  const navigateExplore = () => {
    navigate(Routes.community.index);
  };
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          alignItems: 'center',
        }}
      >
        <img
          src='/public/assets/icons/ic_community.svg'
          width={80}
          height={80}
        />
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          <FormattedMessage id='community.messages.empty.welcome' />
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#626a7a' }}>
          <FormattedMessage id='community.messages.empty.no-connection' />
        </Typography>
        <Button
          onClick={navigateExplore}
          disableElevation
          variant='contained'
          sx={{ px: 14, py: 4, borderRadius: 7.5 }}
        >
          <FormattedMessage id='community.messages.empty.explore' />
        </Button>
      </Box>
    </Box>
  );
};
